import { useEffect, useRef, useState } from 'react';
import { DEDICATION_MAX, type PublicTrack, type SearchTrack } from '@karozik/shared';
import { Artwork, cn, Spinner, TrackLine, useTrackSearch } from '@karozik/ui';
import type { SearchAuth } from './Party';

const UNAVAILABLE: Record<NonNullable<SearchTrack['unavailable']>, string> = {
  playing: 'En cours de lecture',
  queued: 'Déjà dans la file : vote pour lui !',
  played: 'Déjà passé ce soir, indisponible pour le moment',
};

const SUGGESTIONS = ['Daft Punk', 'ABBA', 'Goldman', 'Queen', 'Claude François', 'Stromae', 'Indochine', 'Michael Jackson'];

export function SearchSheet({
  auth,
  big,
  onClose,
  onPropose,
}: {
  auth: SearchAuth;
  big: boolean;
  onClose(): void;
  onPropose(trackId: string, dedication: string | null): Promise<{ ok: boolean; error?: string }>;
}) {
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<PublicTrack | null>(null);
  const { result, loading, error } = useTrackSearch(query, auth);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    input.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-night/95 backdrop-blur-sm" role="dialog" aria-modal aria-label="Proposer un titre">
      <div className={cn('mx-auto flex w-full flex-1 flex-col overflow-hidden', big ? 'max-w-3xl px-8 pt-8' : 'max-w-md px-4 pt-4')}>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <span className="absolute top-1/2 left-4 -translate-y-1/2 text-ink-faint">🔎</span>
            <input
              ref={input}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Titre, artiste…"
              enterKeyHint="search"
              className={cn('w-full rounded-2xl border border-line bg-surface pr-4 pl-11 outline-none focus:border-pink', big ? 'py-5 text-2xl' : 'py-3.5 text-lg')}
            />
          </div>
          <button onClick={onClose} className={cn('rounded-2xl bg-surface-2 font-semibold', big ? 'px-6 py-5 text-xl' : 'px-4 py-3.5')}>
            Fermer
          </button>
        </div>

        <div className="mt-4 flex-1 overflow-y-auto pb-8">
          {query.trim().length < 2 && (
            <div>
              <p className="mb-3 text-sm text-ink-faint">Une idée ?</p>
              <div className="flex flex-wrap gap-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => setQuery(s)} className={cn('rounded-full bg-surface-2 px-4 py-2', big && 'text-lg')}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {loading && (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          )}
          {error && <p className="rounded-xl bg-danger/15 px-4 py-3 text-danger">{error}</p>}
          {result?.limited && (
            <p className="mb-3 rounded-xl bg-sun/15 px-4 py-2 text-sm text-sun">
              Beaucoup de recherches en ce moment : seuls les titres déjà connus s’affichent. Réessaie dans une minute.
            </p>
          )}
          {result && !loading && result.tracks.length === 0 && <p className="py-6 text-center text-ink-dim">Rien trouvé 🤷</p>}
          <ul className="flex flex-col gap-2">
            {result?.tracks.map((t) =>
              t.unavailable ? (
                <li key={t.id} className="rounded-2xl bg-surface p-2.5 opacity-50" aria-disabled>
                  <TrackLine
                    track={t}
                    artSize={big ? 'h-16 w-16' : 'h-12 w-12'}
                    sub={<div className="mt-0.5 text-xs font-semibold text-sun">{UNAVAILABLE[t.unavailable]}</div>}
                  />
                </li>
              ) : (
                <li key={t.id} className="rounded-2xl bg-surface p-2.5">
                  <TrackLine track={t} artSize={big ? 'h-16 w-16' : 'h-12 w-12'} onClick={() => setPicked(t)} right={<span className="text-2xl text-pink">＋</span>} />
                </li>
              ),
            )}
          </ul>
        </div>
      </div>

      {picked && <ConfirmProposal track={picked} big={big} onCancel={() => setPicked(null)} onPropose={onPropose} />}
    </div>
  );
}

function ConfirmProposal({
  track,
  big,
  onCancel,
  onPropose,
}: {
  track: PublicTrack;
  big: boolean;
  onCancel(): void;
  onPropose(trackId: string, dedication: string | null): Promise<{ ok: boolean; error?: string }>;
}) {
  const [dedication, setDedication] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60" onClick={onCancel}>
      <div
        className={cn('animate-pop w-full rounded-t-3xl bg-surface p-5 safe-bottom', big ? 'max-w-3xl p-8' : 'max-w-md')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-4">
          <Artwork track={track} className={big ? 'h-28 w-28' : 'h-20 w-20'} />
          <div className="min-w-0">
            <div className={cn('font-display font-bold', big ? 'text-2xl' : 'text-lg')}>{track.title}</div>
            <div className="text-ink-dim">{track.artist}</div>
          </div>
        </div>
        <label className="mt-5 block">
          <span className="mb-2 block text-sm font-semibold text-ink-dim">💌 Une dédicace ? (affichée sur l’écran géant)</span>
          <input
            value={dedication}
            onChange={(e) => setDedication(e.target.value)}
            maxLength={DEDICATION_MAX}
            placeholder="Pour les mariés ! / Spéciale dédicace à Mamie…"
            className="w-full rounded-2xl border border-line bg-surface-2 px-4 py-3 outline-none focus:border-pink"
          />
        </label>
        {error && <p className="mt-3 rounded-xl bg-danger/15 px-4 py-3 text-sm text-danger">{error}</p>}
        <div className="mt-5 flex gap-3">
          <button onClick={onCancel} className="flex-1 rounded-2xl bg-surface-2 py-4 font-semibold">
            Annuler
          </button>
          <button
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              const r = await onPropose(track.id, dedication.trim() || null);
              setBusy(false);
              if (!r.ok) setError(r.error ?? 'Impossible d’ajouter ce titre.');
            }}
            className="bg-gradient-neon flex-[2] rounded-2xl py-4 font-bold disabled:opacity-50"
          >
            {busy ? <Spinner /> : 'Ajouter à la file 🎶'}
          </button>
        </div>
      </div>
    </div>
  );
}
